import { FC, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import RolesField from 'src/components/InputFields/RoleDropdown';
import { useFormik } from 'formik';
import {
  actionButtons,
  dataStorageKeywords,
  userDialogTitles,
  userDialogSubtitles,
  errorMessages,
  userRoles,
} from 'src/utils/properties.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { User, Role } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  editUserInitialValues,
  adminResetPasswordInitialValues,
} from 'src/utils/initialValues.utils';
import {
  userValidationSchema,
  resetPasswordValidationSchema,
} from 'src/utils/validationScheme.utils';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  adminResetPassword,
  deleteUserById,
  editUser,
  getCompanyRoles,
  toggleUserActivation,
} from 'src/services/user.service';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import {
  FlexContainerBox,
  FlexRowStartBox,
  FlexColumnBox,
  ResetContainerBox,
  ResetPasswordRowBox,
} from '../DialogBox/POBox/POBox.styles';

type ActionMenuButtonProps = {
  user: User;
  incrementRender: () => void;
};

const UserActionMenuButton: FC<ActionMenuButtonProps> = ({
  user,
  incrementRender,
}) => {
  const { id, email, firstName, lastName, roles, isActive } = user;
  const { showSnackbar } = StyledSnackbar();
  const loggedInUserId = Cookies.get(dataStorageKeywords.USER_ID);
  const [allRoles, setAllRoles] = useState<{ value: string; label: string }[]>(
    []
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [targetUserRoles, setTargetUserRoles] = useState<string[]>([]);
  const [isEditingOwnAccount, setIsEditingOwnAccount] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isEditingDisabled =
    currentUserRole !== userRoles.OWNER &&
    targetUserRoles.includes(userRoles.OWNER);

  const isOwnAccount = loggedInUserId === String(id);

  useEffect(() => {
    fetchCurrentUserRole();
    fetchTargetUserRoles();
  }, [email]);

  const fetchAllRoles = async () => {
    try {
      const rolesData = await getCompanyRoles();
      setAllRoles(rolesData);
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    }
  };

  const fetchCurrentUserRole = async () => {
    const role = await getEncryptedLocalStorageData(
      dataStorageKeywords.HIGHEST_ROLE
    );
    setCurrentUserRole(role);
  };

  const fetchTargetUserRoles = async () => {
    setTargetUserRoles(
      roles
        ? roles.map((role: Role | string) =>
            typeof role === 'string' ? role : role.name
          )
        : []
    );
  };

  const handleResetPasswordSubmit = async () => {
    try {
      const response = await adminResetPassword({
        id: String(id),
        requesterUserId: loggedInUserId || '',
        newPassword: resetPasswordFormik.values.newPassword,
      });
      setResetPasswordDialogOpen(false);
      resetPasswordFormik.resetForm();
      showSnackbar(response.message, 'success');
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditFormSubmit = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      const response = await editUser({
        id: selectedUser.id,
        requesterUserId: loggedInUserId || '',
        email: formik.values.email,
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        roles: formik.values.role,
      });
      setEditDialogOpen(false);
      incrementRender();
      showSnackbar(response.message, 'success');
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async () => {
    try {
      await fetchAllRoles();

      const loggedInUserID = Cookies.get(dataStorageKeywords.USER_ID);
      if (loggedInUserID && id && loggedInUserID === String(id)) {
        setIsEditingOwnAccount(true);
      }

      const normalizedRoles = Array.isArray(roles)
        ? roles.map((role: Role | string) =>
            typeof role === 'string' ? role : role.name
          )
        : [];

      setSelectedUser(user);

      formik.setValues({
        email,
        firstName,
        lastName,
        role: normalizedRoles,
      });

      setEditDialogOpen(true);
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogClose = () => {
    try {
      setEditDialogOpen(false);
      setSelectedUser(null);
      formik.resetForm();
    } catch (error) {
      showSnackbar(errorMessages.ERROR_CLOSING_DIALOG, 'error');
    }
  };

  const handleResetPasswordDialogOpen = () => {
    setResetPasswordDialogOpen(true);
    handleClose();
  };

  const handleResetPasswordDialogClose = () => {
    setResetPasswordDialogOpen(false);
    resetPasswordFormik.resetForm();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setSelectedEmail(email);
    setSelectedUserId(String(id));
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    if (!selectedEmail && !selectedUserId) return;

    try {
      if (dialogType === actionButtons.DELETE) {
        const response = await deleteUserById(selectedUserId || '');
        showSnackbar(response.message, 'success');
        incrementRender();
      } else if (
        dialogType === actionButtons.ACTIVATE ||
        dialogType === actionButtons.DEACTIVATE
      ) {
        const response = await toggleUserActivation(
          selectedUserId || '',
          dialogType === actionButtons.ACTIVATE
        );
        showSnackbar(response.message, 'success');
        incrementRender();
      }
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setSelectedEmail(null);
      setSelectedUserId(null);
    }
  };

  const formik = useFormik({
    initialValues: editUserInitialValues,
    validationSchema: userValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  const resetPasswordFormik = useFormik({
    initialValues: adminResetPasswordInitialValues,
    validationSchema: resetPasswordValidationSchema,
    onSubmit: handleResetPasswordSubmit,
  });

  return (
    <>
      <ActionMenuButton
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          !(isEditingDisabled || isOwnAccount) &&
            setAnchorEl(event.currentTarget);
        }}
        component="i"
        className="bx bx-dots-horizontal-rounded"
        style={{
          color:
            isEditingDisabled || isOwnAccount
              ? 'var(--border)'
              : 'var(--border-blue)',
          pointerEvents: isEditingDisabled || isOwnAccount ? 'none' : 'auto',
        }}
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={() => handleEditDialogOpen()}
            iconClassName="bx bx-edit"
            text="Edit"
          />

          {!isOwnAccount && !targetUserRoles.includes(userRoles.OWNER) && (
            <MenuItemWithIcon
              onClick={() => handleResetPasswordDialogOpen()}
              iconClassName="bx bx-key"
              text="Reset Password"
            />
          )}

          {!isOwnAccount && (
            <>
              <MenuItemWithIcon
                onClick={() =>
                  handleDialogOpen(
                    isActive ? actionButtons.DEACTIVATE : actionButtons.ACTIVATE
                  )
                }
                iconClassName={
                  isActive ? 'bx bx-minus-circle' : 'bx bx-radio-circle-marked'
                }
                text={isActive ? 'Deactivate' : 'Activate'}
              />

              <MenuItemWithIcon
                onClick={() => handleDialogOpen(actionButtons.DELETE)}
                iconClassName="bx bx-trash"
                text="Delete"
              />
            </>
          )}
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <CustomDialog
        height={'480px'}
        open={editDialogOpen}
        handleClose={handleEditDialogClose}
        title={userDialogTitles.EDIT}
        subtitle={userDialogSubtitles.EDIT}
        onSubmit={formik.handleSubmit}
        smScreen={false}
        buttonLabel="Update"
      >
        {selectedUser && (
          <FlexContainerBox>
            <FlexRowStartBox>
              <FlexColumnBox>
                <TextField
                  id="firstName"
                  label="First Name"
                  formikProps={formik}
                  isEditingOwnAccount={isEditingOwnAccount}
                  width="294px"
                />
              </FlexColumnBox>
              <FlexColumnBox>
                <TextField
                  id="lastName"
                  label="Last Name"
                  formikProps={formik}
                  isEditingOwnAccount={isEditingOwnAccount}
                  width="294px"
                />
              </FlexColumnBox>
            </FlexRowStartBox>

            <FlexRowStartBox>
              <FlexColumnBox>
                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  formikProps={formik}
                  isEditingOwnAccount={isEditingOwnAccount}
                  width="294px"
                />
              </FlexColumnBox>
              <FlexColumnBox>
                <RolesField
                  id="role"
                  label="Role"
                  formikProps={formik}
                  options={allRoles}
                  userRole={currentUserRole || ''}
                  isEditingOwnAccount={isEditingOwnAccount}
                  width="294px"
                />
              </FlexColumnBox>
            </FlexRowStartBox>
          </FlexContainerBox>
        )}
      </CustomDialog>

      <CustomDialog
        height={'350px'}
        open={resetPasswordDialogOpen}
        handleClose={handleResetPasswordDialogClose}
        title={userDialogTitles.RESET_PASSWORD}
        subtitle={userDialogSubtitles.RESET_PASSWORD}
        onSubmit={resetPasswordFormik.handleSubmit}
        smScreen={false}
        buttonLabel="Reset"
      >
        <ResetContainerBox>
          <ResetPasswordRowBox>
            <FlexColumnBox>
              <TextField
                id="newPassword"
                label="New Password"
                type="password"
                showPasswordToggle={true}
                formikProps={resetPasswordFormik}
                width="294px"
              />
            </FlexColumnBox>
            <FlexColumnBox>
              <TextField
                id="confirmPassword"
                label="Confirm Password"
                type="password"
                showPasswordToggle={true}
                formikProps={resetPasswordFormik}
                width="294px"
              />
            </FlexColumnBox>
          </ResetPasswordRowBox>
        </ResetContainerBox>
      </CustomDialog>

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={
          dialogType === actionButtons.DELETE
            ? userDialogTitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? userDialogTitles.ACTIVATE
            : userDialogTitles.DEACTIVATE
        }
        subtitle={
          dialogType === actionButtons.DELETE
            ? userDialogSubtitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? userDialogSubtitles.ACTIVATE
            : userDialogSubtitles.DEACTIVATE
        }
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--success)'
            : 'var(--error)'
        }
        alertIconColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--warning)'
            : 'var(--error)'
        }
      />
    </>
  );
};
export default UserActionMenuButton;
